import { useState } from 'react'
import {
  LayoutDashboard,
  LayoutGrid,
  MessageSquare,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  PieChart,
  Plus,
  Trash2,
  TrendingUp,
  Users,
} from 'lucide-react'
import type { Dashboard } from '../types'
import { plural } from '../format'
import type { SectionId } from '../sections'
import type { AuthUser } from '../api/auth'
import { PMark } from './Logo'
import { TENANT } from '../tenant'
import { USER_MGMT_UI_ENABLED } from '../flags'
import { previewingSupplier } from '../chatAccess'

const initialsOf = (name: string) =>
  name.trim().split(/\s+/).map((w) => w[0]?.toUpperCase() ?? '').slice(0, 2).join('') || '?'

// The standard sections, as the sidebar's primary nav. "My dashboards" is one of
// these — the dashboard builder is a native FUNCTION of the tool, not a separate
// system; the user's boards nest beneath it. The AI chat is NOT in the nav: it's a
// function reached from the docked ask-bar, not the product itself.
const SECTIONS: { id: SectionId; label: string; Icon: typeof LayoutDashboard }[] = [
  { id: 'oversikt', label: 'Overview', Icon: LayoutDashboard },
  { id: 'trends', label: 'Sales Trends', Icon: TrendingUp },
  { id: 'forsaljning', label: 'Top lists', Icon: TrendingUp },
  { id: 'marknad', label: 'Packages', Icon: PieChart },
]

// Placeholder account identity — a seam for when real auth/SSO lands (no login yet).
export function Sidebar({
  user,
  onLogout,
  dashboards,
  activeSection,
  chatEnabled,
  activeId,
  collapsed,
  dropActive,
  onToggle,
  onSelectSection,
  onSelect,
  onNew,
  onDelete,
  onDropToBoard,
}: {
  user: AuthUser
  onLogout: () => void
  dashboards: Dashboard[]
  activeSection: SectionId
  chatEnabled: boolean // MP-OS-193: false for suppliers and an admin's supplier preview
  activeId: string | null
  collapsed: boolean
  dropActive: boolean
  onToggle: () => void
  onSelectSection: (id: SectionId) => void
  onSelect: (id: string) => void
  onNew: () => void
  onDelete: (id: string) => void
  onDropToBoard: (boardId: string) => void
}) {
  const [overId, setOverId] = useState<string | null>(null)
  const inMina = activeSection === 'mina'

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-top">
        <div
          className="brandmark"
          onClick={() => onSelectSection('oversikt')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onSelectSection('oversikt')
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="To Overview"
        >
          {collapsed ? (
            <PMark size={26} />
          ) : (
            <img src="/minprilla-logo.png" alt={TENANT.productName} className="brand-logo" />
          )}
        </div>
        <button className="collapse" onClick={onToggle} aria-label={collapsed ? 'Show menu' : 'Hide menu'}>
          {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
      </div>

      {!collapsed && (
        <nav className="sidebar-nav">
          <div className="nav-section">Explore</div>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              className={`nav-item ${activeSection === s.id ? 'active' : ''}`}
              onClick={() => onSelectSection(s.id)}
            >
              <s.Icon size={16} /> <span className="board-label">{s.label}</span>
            </button>
          ))}

          {user.role === 'admin' && !previewingSupplier() && <button
            className={`nav-item ${activeSection === 'business' ? 'active' : ''}`}
            onClick={() => onSelectSection('business')}
          ><TrendingUp size={16} /><span className="board-label">Business metrics</span></button>}

          {/* Mina dashboards — a function under Utforska. The AI chat lives here (the
              build tool), with the user's boards nested below it. */}
          <button
            className={`nav-item ${inMina ? 'active' : ''}`}
            onClick={() => onSelectSection('mina')}
          >
            <LayoutGrid size={16} /> <span className="board-label">My dashboards</span>
          </button>
          <div className="nav-subgroup">
            {chatEnabled && <button
              className={`nav-item sub ${inMina && !activeId ? 'active' : ''}`}
              onClick={() => onSelectSection('mina')}
            >
              <MessageSquare size={15} /> <span className="board-label">Chat</span>
            </button>}
            {dashboards.length === 0 && <p className="nav-empty">
              {chatEnabled ? 'Pin an answer to get started.' : 'No dashboards yet.'}</p>}
            {dashboards.map((d) => (
              <div
                key={d.id}
                className={`nav-item board sub ${inMina && activeId === d.id ? 'active' : ''} ${
                  dropActive ? 'droppable' : ''
                } ${overId === d.id ? 'dragover' : ''}`}
                onDragOver={(e) => {
                  if (dropActive) {
                    e.preventDefault()
                    setOverId(d.id)
                  }
                }}
                onDragLeave={() => setOverId((cur) => (cur === d.id ? null : cur))}
                onDrop={(e) => {
                  e.preventDefault()
                  setOverId(null)
                  onDropToBoard(d.id)
                }}
              >
                <button className="board-name" onClick={() => onSelect(d.id)} title={d.name} aria-label={`${d.name} (${d.cards.length} ${plural(d.cards.length, 'card')})`}>
                  <span className="board-label">{d.name}</span>
                  <span className="board-count">{d.cards.length}</span>
                </button>
                <button
                  className="board-del"
                  onClick={() => onDelete(d.id)}
                  aria-label={`Remove ${d.name}`}
                  title="Remove dashboard"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button className="nav-item new sub" onClick={onNew}>
              <Plus size={15} /> New dashboard
            </button>
            {dropActive && <p className="drop-hint">Drop on a dashboard to pin</p>}
          </div>
        </nav>
      )}

      {!collapsed && (
        <>
          <div className="sidebar-account">
            <span className="acct-avatar">{initialsOf(user.name)}</span>
            <span className="acct-meta">
              <span className="acct-name">{user.name}</span>
              <span className="acct-role">Signed in</span>
            </span>
            {/* Admin panel (MP-OS-93). Tucked under the name, not in Explore — Olof 4/9:
                "under ditt namn som en adminpanel, så den ska inte vara så synlig". */}
            {USER_MGMT_UI_ENABLED && user.role === 'admin' && (
              <button
                className={`acct-gear ${activeSection === 'admin' ? 'active' : ''}`}
                onClick={() => onSelectSection('admin')}
                aria-label="Manage users"
                title="Manage users (admin)"
              >
                <Users size={16} />
              </button>
            )}
            <button className="acct-gear" onClick={onLogout} aria-label="Sign out" title="Sign out">
              <LogOut size={16} />
            </button>
          </div>
          <div className="sidebar-foot">
            <span>Powered by</span>
            <a
              href="https://solvigo.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="solvigo-link"
              aria-label="Solvigo – visit the website"
            >
              <img src="/solvigo-white.svg" alt="Solvigo" className="solvigo-logo" />
            </a>
          </div>
        </>
      )}
    </aside>
  )
}
